import { ColumnDef } from '@tanstack/react-table';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { History, Trash2, Eye, ArrowUpDown, UserRoundCog } from 'lucide-react';
import type { ApiAssignment } from '@/components/AssignmentTable';

export interface AssignmentColumnHandlers {
  onStatusChange?: (assignmentId: number, newStatus: string) => void;
  onViewHistory?: (assignmentId: number) => void;
  onDelete?: (assignmentId: number) => void;
  onViewEvidence?: (assignmentId: number) => void;
  onReassign?: (assignment: ApiAssignment) => void;  // Phase 4: Reassignment handler
  getStatusBadge?: (assignment: ApiAssignment | string) => React.ReactNode;
}

export function createAssignmentColumns(
  handlers: AssignmentColumnHandlers
): ColumnDef<ApiAssignment>[] {
  const {
    onStatusChange,
    onViewHistory,
    onDelete,
    onViewEvidence,
    onReassign,
    getStatusBadge,
  } = handlers;

  return [
    // Select column
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    // ID column
    {
      accessorKey: 'id',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            ID
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <span className="font-mono text-sm">{row.original.id}</span>
      ),
    },
    // Phase 4: Task Type column
    {
      accessorKey: 'taskType',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Type
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const taskType = row.original.taskType || 'POSTING';
        return (
          <Badge
            variant={taskType === 'REMOVAL' ? 'destructive' : 'default'}
            className={taskType === 'REMOVAL' ? 'bg-orange-100 text-orange-800 hover:bg-orange-100' : 'bg-blue-100 text-blue-800 hover:bg-blue-100'}
          >
            {taskType}
          </Badge>
        );
      },
    },
    // Campaign column
    {
      accessorKey: 'campaignName',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Campaign
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const campaignName = row.original.campaignName || 'Unknown Campaign';
        // Phase 4: For removal tasks, show the original campaign(s) from enriched data
        if (row.original.taskType === 'REMOVAL') {
          const campaigns = row.original.removalCampaigns as string[] | undefined;
          if (campaigns && campaigns.length > 0) {
            return campaigns.length > 2 
              ? `${campaigns.slice(0, 2).join(', ')} +${campaigns.length - 2} more`
              : campaigns.join(', ');
          }
        }
        return campaignName;
      },
    },
    // Store column
    {
      accessorKey: 'storeName',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Store
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => row.original.storeName || 'Unknown Store',
    },
    // Operator column
    {
      accessorKey: 'operatorName',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Operator
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => row.original.operatorName || 'Unknown Operator',
    },
    // Task column
    {
      accessorKey: 'taskName',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Task
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) =>
        row.original.taskName || row.original.packName || 'Unknown Task',
    },
    // Status column
    {
      accessorKey: 'status',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Status
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const assignmentId = Number(row.original.id);

        if (onStatusChange) {
          return (
            <Select
              value={row.original.status || ''}
              onValueChange={(value) => onStatusChange(assignmentId, value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="operator_complete">Operator Complete</SelectItem>
                <SelectItem value="admin_complete">Admin Complete</SelectItem>
                <SelectItem value="admin_rejected">Admin Rejected</SelectItem>
                <SelectItem value="locked">Locked</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          );
        }

        return getStatusBadge
          ? getStatusBadge(row.original)
          : row.original.status;
      },
    },
    // Quantity column - shows format breakdown for all task types
    {
      id: 'quantity',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Quantity
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        // Phase 4: Format breakdown helper
        const renderFormatBreakdown = (
          items: Array<{ formatName: string; qty: number }> | undefined,
          totalQty: number | undefined
        ) => {
          if (items && items.length > 0) {
            // Group by format and sum quantities
            const formatTotals = items.reduce((acc, item) => {
              acc[item.formatName] = (acc[item.formatName] || 0) + item.qty;
              return acc;
            }, {} as Record<string, number>);
            
            const formatList = Object.entries(formatTotals)
              .map(([name, qty]) => `${qty}× ${name}`)
              .join(', ');
            
            return (
              <span title={formatList} className="cursor-help">
                {totalQty || 0} items
                <span className="text-xs text-gray-500 block truncate max-w-[150px]">
                  {formatList}
                </span>
              </span>
            );
          }
          return totalQty || 0;
        };

        // For REMOVAL tasks
        if (row.original.taskType === 'REMOVAL') {
          const items = row.original.removalItems as Array<{ formatName: string; qty: number }> | undefined;
          const totalQty = row.original.removalTotalQty as number | undefined;
          return renderFormatBreakdown(items, totalQty);
        }
        
        // For POSTING tasks
        if (row.original.taskType === 'POSTING') {
          const items = row.original.postingItems as Array<{ formatName: string; qty: number }> | undefined;
          const totalQty = row.original.postingTotalQty as number | undefined;
          if (items && items.length > 0) {
            return renderFormatBreakdown(items, totalQty);
          }
        }
        
        // Fallback to raw quantity
        return row.original.quantity ||
          row.original.expectedQuantity ||
          row.original.targetQuantity ||
          0;
      },
    },
    // Actions column
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const assignmentId = Number(row.original.id);
        const assignment = row.original;

        // Phase 4: Determine if reassignment is allowed
        const completedStatuses = ['operator_complete', 'admin_complete', 'locked', 'archived'];
        const canReassign = onReassign && !completedStatuses.includes(assignment.status || '');

        return (
          <div className="flex gap-1">
            {onViewHistory && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewHistory(assignmentId)}
                title="View History"
              >
                <History className="h-4 w-4" />
              </Button>
            )}
            {/* Phase 4: Reassign button - only for non-completed tasks */}
            {canReassign && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onReassign(assignment)}
                title="Reassign Operator"
              >
                <UserRoundCog className="h-4 w-4" />
              </Button>
            )}
            {onDelete && !assignment.isDeleted && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(assignmentId)}
                title="Delete"
                className="text-red-600 hover:text-red-800"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            {assignment.status === 'operator_complete' && onViewEvidence && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewEvidence(assignmentId)}
                title="View Evidence"
              >
                <Eye className="h-4 w-4" />
              </Button>
            )}
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
  ];
}
